import { Form, FormikProvider, useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import Loader3D from '../../components/Loader/Loader3D';
import { CancelButton, SubmitButton } from '../../components/mui/Buttons/Buttons';
import { Box, Stack, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getAttendanceTypesForAcademicCalendar, markAttendanceAcademicCalendar } from '../../redux/features/CRDslice';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import GlobalSelectField from '../../components/mui/GlobalSelectField';

const MarkAttendanceModalContent = ({ row, apiCall, dispatchParams, setOpen, open }) => {
  const dispatch = useDispatch();
  const { loading, attendanceTypes } = useSelector((state) => state.crd);

  useEffect(() => {
    if (open && row?.subject_enrollment_id) {
      dispatch(getAttendanceTypesForAcademicCalendar(row?.subject_enrollment_id));
    }
  }, [open, row?.subject_enrollment_id]);

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    return dayjs(timeStr, 'HH:mm:ss');
  };

  const { enqueueSnackbar } = useSnackbar();
  const initialValues = {
    session_schedule_id: row?.session_schedule_id ?? '',
    subject_enrollment_id: row?.subject_enrollment_id ?? '',
    attendanceDate: row?.session_date ?? '',
    attendance_type: null,
    fromTime: parseTime(row?.from_time),
    toTime: parseTime(row?.to_time),
    lesson: '',
    trainer_remarks: '',
    fixed_tp: null,
  };

  const schema = Yup.object({
    attendance_type: Yup.mixed().required('Attendance type is required'),
    fromTime: Yup.date().required('From time is required'),
    toTime: Yup.date().required('To time is required'),
    lesson: Yup.string().required('Lesson is required'),
    trainer_remarks: Yup.string().required('Trainer remarks is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        fromTime: values.fromTime ? dayjs(values.fromTime).format('HH:mm:ss') : null,
        toTime: values.toTime ? dayjs(values.toTime).format('HH:mm:ss') : null,
      };
      dispatch(markAttendanceAcademicCalendar(payload)).then((res) => {
        if (res.payload.status === 'error') {
          enqueueSnackbar(res.payload.message, {
            variant: 'error',
          });
        } else if (res.payload.status === 'success') {
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });
          dispatch(apiCall(dispatchParams));
          handleCancel();
        }
      });
    },
  });

  useEffect(() => {
    if (formik.values.attendance_type) {
      const selectedAttendanceType = attendanceTypes.find(
        (type) => type.course_attendance_type_id === formik.values.attendance_type,
      );
      formik.setFieldValue('fixed_tp', selectedAttendanceType?.fixed_tp || null);
    }
  }, [formik.values.attendance_type]);
  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <>
      {open ? (
        loading ? (
          <Loader3D />
        ) : (
          <>
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Box>
                  <Stack gap={2}>
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Attendance Date"
                          format="DD-MM-YYYY"
                          value={formik.values.attendanceDate ? dayjs(formik.values.attendanceDate) : null}
                          onChange={(value) =>
                            formik.setFieldValue('attendanceDate', value ? dayjs(value).format('YYYY-MM-DD') : null)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: formik.touched.attendanceDate && Boolean(formik.errors.attendanceDate),
                              helperText: formik.touched.attendanceDate && formik.errors.attendanceDate,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                    <GlobalSelectField
                      options={{
                        formik,
                        name: 'attendance_type',
                        label: 'Attendance Type',
                        data: attendanceTypes,
                        keys: {
                          id: 'course_attendance_type_id',
                          value: 'attendance_type_name',
                        },
                        sx: { width: '100%' },
                      }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        label="From Time"
                        value={formik.values.fromTime}
                        onChange={(newValue) => formik.setFieldValue('fromTime', newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />

                      <TimePicker
                        label="To Time"
                        value={formik.values.toTime}
                        onChange={(newValue) => formik.setFieldValue('toTime', newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <TextField
                      fullWidth
                      label="Lesson"
                      name="lesson"
                      value={formik.values.lesson}
                      onChange={formik.handleChange}
                      error={formik.touched.lesson && Boolean(formik.errors.lesson)}
                      helperText={formik.touched.lesson && formik.errors.lesson}
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Notes / Trainer Remarks"
                      name="trainer_remarks"
                      value={formik.values.trainer_remarks}
                      onChange={formik.handleChange}
                      error={formik.touched.trainer_remarks && Boolean(formik.errors.trainer_remarks)}
                      helperText={formik.touched.trainer_remarks && formik.errors.trainer_remarks}
                    />
                  </Stack>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Stack direction={'row'} spacing={1}>
                    <CancelButton action={() => handleCancel()}>Cancel</CancelButton>

                    <SubmitButton disabled={loading} type="submit">
                      {loading ? 'Submiting...' : 'Submit'}
                    </SubmitButton>
                  </Stack>
                </Box>
              </Form>
            </FormikProvider>
          </>
        )
      ) : null}
    </>
  );
};

export default MarkAttendanceModalContent;
